import { findQueryItemNameGroup } from "../../js/query/item-name-groups.js";
import { isPlainObject } from "./shared.js";
import {
  QUERY_MATCH_RANK_EXACT,
  QUERY_MATCH_RANK_FUZZY,
  QUERY_MATCH_RANK_NONE,
  QUERY_MATCH_RANK_SUBSTRING,
  compactQueryMatchText,
  createPreparedQueryMatchCacheTransaction,
  flattenQueryMatchValues,
  matchQueryValues,
  normalizeQueryMatchText,
  queryMatchTokens,
  slugifyQueryMatchText,
} from "../../js/query/match.js";
import {
  createQueryCatalogIndexCache,
  matchingQueryCatalogRecords,
  resolveQueryCatalogReference,
} from "../../js/query/catalog-index.js";
import { isSearchExpressionReservedKeywordPrefix } from "./constants.js";
import { TRADE_NPC_LAYER_ID } from "../map-trade-npc-contract.js";
import {
  normalizePatchId,
  tradeNpcKeyFromSearchTerm,
  tradeNpcSearchTermForKey,
} from "./terms.js";

export const normalizeSearchExpressionLookupText = normalizeQueryMatchText;
export const slugifySearchExpressionLookupText = slugifyQueryMatchText;
export const compactSearchExpressionLookupText = compactQueryMatchText;

function searchExpressionLookupTokens(value) {
  return queryMatchTokens(value);
}

export function parseSearchExpressionPositiveInteger(value) {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function parseSearchExpressionZoneRgb(value) {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return null;
  }
  const triplet = raw.match(/^(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})$/);
  if (triplet) {
    const bytes = triplet.slice(1).map(Number);
    return bytes.every(byte => byte <= 255)
      ? (bytes[0] << 16) | (bytes[1] << 8) | bytes[2]
      : null;
  }
  const hex = raw.match(/^(?:#|0x)([0-9a-f]{6})$/i);
  if (hex) {
    return Number.parseInt(hex[1], 16);
  }
  if (/^[0-9a-f]{6}$/i.test(raw) && /[a-f]/i.test(raw)) {
    return Number.parseInt(raw, 16);
  }
  const parsed = /^\d+$/.test(raw)
    ? Number.parseInt(raw, 10)
    : null;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function catalogEntries(options, keys) {
  for (const key of keys) {
    const value = options?.[key] ?? options?.catalog?.[key];
    if (Array.isArray(value)) {
      return { available: true, entries: value };
    }
  }
  return { available: false, entries: [] };
}

function lookupValues(value) {
  return flattenQueryMatchValues(value);
}

function lookupValueMatchesExact(value, normalized, slug) {
  return lookupValues(value).some((candidate) =>
    normalizeSearchExpressionLookupText(candidate) === normalized ||
    slugifySearchExpressionLookupText(candidate) === slug
  );
}

const LOOKUP_MATCH_NONE = QUERY_MATCH_RANK_NONE;
const LOOKUP_MATCH_FUZZY = QUERY_MATCH_RANK_FUZZY;
const LOOKUP_MATCH_SUBSTRING = QUERY_MATCH_RANK_SUBSTRING;
const LOOKUP_MATCH_EXACT = QUERY_MATCH_RANK_EXACT;

function lookupValueMatchRank(value, normalized, slug) {
  return matchQueryValues(value, normalized, { slug }).rank;
}

function lookupValueMatchesFuzzy(value, normalized, slug) {
  return lookupValueMatchRank(value, normalized, slug) > LOOKUP_MATCH_NONE;
}

function lookupCandidateMatch(value, normalized, slug, queryTokens = []) {
  return {
    ...matchQueryValues(value, normalized, { slug }),
    queryTokenCount: Array.isArray(queryTokens) ? queryTokens.length : 0,
  };
}

function resolveCatalogId(rawValue, options, catalogKeys, idKeys, nameKeys) {
  const numeric = parseSearchExpressionPositiveInteger(rawValue);
  if (numeric != null) {
    return { status: "resolved", id: numeric };
  }

  const normalized = normalizeSearchExpressionLookupText(rawValue);
  const slug = slugifySearchExpressionLookupText(rawValue);
  if (!normalized && !slug) {
    return { status: "invalid" };
  }

  const catalog = catalogEntries(options, catalogKeys);
  if (!catalog.available) {
    return { status: "catalog_unavailable" };
  }

  const fuzzyMatches = new Map();
  for (const entry of catalog.entries) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const id = idKeys
      .map((key) => parseSearchExpressionPositiveInteger(entry[key]))
      .find((candidate) => candidate != null);
    if (id == null) {
      continue;
    }
    for (const key of nameKeys) {
      const candidate = entry[key];
      if (lookupValueMatchesExact(candidate, normalized, slug)) {
        return { status: "resolved", id };
      }
      if (lookupValueMatchesFuzzy(candidate, normalized, slug)) {
        fuzzyMatches.set(id, id);
      }
    }
  }
  if (fuzzyMatches.size === 1) {
    return { status: "resolved", id: [...fuzzyMatches.keys()][0] };
  }
  return { status: "unknown" };
}

function catalogHasAvailableEntries(options, catalogKeys) {
  return catalogEntries(options, catalogKeys).available;
}

function zoneCatalogEntries(options) {
  return catalogEntries(options, ["zoneCatalog", "catalogZones", "zones"]);
}

function zoneEntryRgb(entry) {
  return parseSearchExpressionZoneRgb(
    entry?.zoneRgb ?? entry?.zone_rgb ?? entry?.rgb ?? entry?.value ?? entry?.id,
  );
}

function zoneEntryLabelLookupValues(entry) {
  return [
    entry?.name,
    entry?.label,
    entry?.zoneName,
    entry?.zone_name,
    ...(Array.isArray(entry?.labels) ? entry.labels : []),
  ];
}

function zoneEntryStructuredLookupValues(entry) {
  const zoneRgb = zoneEntryRgb(entry);
  const hex = zoneRgb != null ? zoneRgb.toString(16).padStart(6, "0") : "";
  return [
    entry?.rgbKey,
    entry?.rgbSpaced,
    entry?.normalizedKey,
    entry?.normalizedSpaced,
    entry?.hexKey,
    entry?.hashHexKey,
    entry?.bareHexKey,
    zoneRgb,
    hex ? `0x${hex}` : "",
    hex ? `#${hex}` : "",
    hex,
  ];
}

function lookupStructuredValueMatchesExact(value, normalized) {
  return lookupValues(value).some((candidate) =>
    normalizeSearchExpressionLookupText(candidate) === normalized
  );
}

function itemEntryIsFish(entry) {
  return entry?.isFish === true ||
    entry?.is_fish === true ||
    entry?.kind === "fish" ||
    entry?.type === "fish";
}

function fishEntryReferenceId(entry, fallback) {
  return [
    entry?.itemId,
    entry?.item_id,
    entry?.fishId,
    entry?.fish_id,
    entry?.id,
    fallback,
  ]
    .map(parseSearchExpressionPositiveInteger)
    .find((id) => id != null) ?? null;
}

function itemEntryReferenceId(entry, fallback) {
  return [
    entry?.itemId,
    entry?.item_id,
    entry?.id,
    entry?.fishId,
    entry?.fish_id,
    fallback,
  ]
    .map(parseSearchExpressionPositiveInteger)
    .find((id) => id != null) ?? null;
}

function positiveIntegerList(values) {
  const seen = new Set();
  const out = [];
  for (const value of Array.isArray(values) ? values : []) {
    const id = parseSearchExpressionPositiveInteger(value);
    if (id == null || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out.sort((left, right) => left - right);
}

function specialItemReferenceEntries(options) {
  return catalogEntries(options, ["specialItemReferences", "specialItems"]);
}

function specialItemReferenceLookupValues(entry) {
  return [
    entry?.aliasItemId,
    entry?.alias_item_id,
    entry?.name,
    entry?.label,
    ...(Array.isArray(entry?.labels) ? entry.labels : []),
    ...(Array.isArray(entry?.lookupValues) ? entry.lookupValues : []),
  ];
}

function resolvedSpecialItemReference(entry) {
  const kind = String(entry?.kind ?? entry?.type ?? "").trim().toLowerCase();
  if (kind === "item-group" || kind === "itemgroup") {
    const itemIds = positiveIntegerList(entry?.itemIds ?? entry?.item_ids);
    if (!itemIds.length) {
      return null;
    }
    return {
      status: "resolved",
      kind: "item-group",
      itemIds,
      sourceKinds: Array.isArray(entry?.sourceKinds) ? entry.sourceKinds : [],
    };
  }
  const itemId = itemEntryReferenceId(entry, entry?.itemId ?? entry?.id);
  if (itemId == null) {
    return null;
  }
  return {
    status: "resolved",
    kind: itemEntryIsFish(entry) ? "fish" : "item",
    id: itemId,
  };
}

function resolveSpecialItemReference(rawValue, options) {
  const catalog = specialItemReferenceEntries(options);
  if (!catalog.available) {
    return null;
  }
  const numeric = parseSearchExpressionPositiveInteger(rawValue);
  const normalized = normalizeSearchExpressionLookupText(rawValue);
  const slug = slugifySearchExpressionLookupText(rawValue);
  const fuzzyMatches = [];

  for (const entry of catalog.entries) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const lookupValues = specialItemReferenceLookupValues(entry);
    const exact = numeric != null
      ? lookupValues.some((value) => parseSearchExpressionPositiveInteger(value) === numeric)
      : lookupValues.some((value) => lookupValueMatchesExact(value, normalized, slug));
    if (exact) {
      return resolvedSpecialItemReference(entry);
    }
    if (numeric == null && lookupValues.some((value) => lookupValueMatchesFuzzy(value, normalized, slug))) {
      const resolved = resolvedSpecialItemReference(entry);
      if (resolved) {
        fuzzyMatches.push(resolved);
      }
    }
  }
  return fuzzyMatches.length === 1 ? fuzzyMatches[0] : null;
}

function resolveFishOrItemReferenceFromCatalog(rawValue, options) {
  const special = resolveSpecialItemReference(rawValue, options);
  if (special) {
    return special;
  }
  const numeric = parseSearchExpressionPositiveInteger(rawValue);
  const fishCatalog = catalogEntries(options, ["fishCatalog", "catalogFish", "fish"]);
  const itemCatalog = catalogEntries(options, ["itemCatalog", "catalogItems", "items"]);
  if (numeric != null) {
    if (fishCatalog.available) {
      for (const entry of fishCatalog.entries) {
        if (!isPlainObject(entry)) {
          continue;
        }
        const ids = [entry.fishId, entry.fish_id, entry.id, entry.itemId, entry.item_id]
          .map(parseSearchExpressionPositiveInteger)
          .filter((id) => id != null);
        if (ids.includes(numeric)) {
          return {
            status: "resolved",
            id: fishEntryReferenceId(entry, numeric) ?? numeric,
            kind: "fish",
          };
        }
      }
    }
    if (itemCatalog.available) {
      for (const entry of itemCatalog.entries) {
        if (!isPlainObject(entry)) {
          continue;
        }
        const ids = [entry.itemId, entry.item_id, entry.id, entry.fishId, entry.fish_id]
          .map(parseSearchExpressionPositiveInteger)
          .filter((id) => id != null);
        if (ids.includes(numeric)) {
          const itemId = itemEntryReferenceId(entry, numeric) ?? numeric;
          return {
            status: "resolved",
            id: itemId,
            kind: itemEntryIsFish(entry) ? "fish" : "item",
          };
        }
      }
    }
    return null;
  }

  const fishResolved = resolveSearchExpressionFishId(rawValue, options);
  if (fishResolved.status === "resolved") {
    const fish = (fishCatalog.entries || []).find((entry) => {
      if (!isPlainObject(entry)) {
        return false;
      }
      return [
        entry.fishId,
        entry.fish_id,
        entry.id,
        entry.itemId,
        entry.item_id,
      ]
        .map(parseSearchExpressionPositiveInteger)
        .includes(fishResolved.id);
    });
    return {
      ...fishResolved,
      id: fishEntryReferenceId(fish, fishResolved.id) ?? fishResolved.id,
      kind: "fish",
    };
  }
  const itemResolved = resolveSearchExpressionItemId(rawValue, options);
  if (itemResolved.status === "resolved") {
    const item = (itemCatalog.entries || []).find((entry) => {
      if (!isPlainObject(entry)) {
        return false;
      }
      return [
        entry.itemId,
        entry.item_id,
        entry.id,
        entry.fishId,
        entry.fish_id,
      ]
        .map(parseSearchExpressionPositiveInteger)
        .includes(itemResolved.id);
    });
    const itemId = itemEntryReferenceId(item, itemResolved.id) ?? itemResolved.id;
    return {
      ...itemResolved,
      id: itemId,
      kind: itemEntryIsFish(item) ? "fish" : "item",
    };
  }
  return fishResolved.status === "catalog_unavailable" && itemResolved.status === "catalog_unavailable"
    ? { status: "catalog_unavailable" }
    : { status: "unknown" };
}

function candidateIdentityKey(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return "";
  }
  if (candidate.term?.kind === "item-group") {
    return `item-group:${positiveIntegerList(candidate.term.itemIds).join(",")}`;
  }
  if (candidate.term?.kind === "semantic") {
    return `semantic:${candidate.term.layerId}:${candidate.term.fieldId}`;
  }
  if (candidate.term?.kind === "zone") {
    return `zone:${candidate.term.zoneRgb}`;
  }
  if (candidate.term?.kind === "fish") {
    return `fish:${candidate.term.fishId}`;
  }
  if (candidate.term?.kind === "item") {
    return `item:${candidate.term.itemId}`;
  }
  const tradeNpcKey = tradeNpcKeyFromSearchTerm(candidate.term);
  if (tradeNpcKey != null) {
    return `trade-npc:${tradeNpcKey}`;
  }
  return "";
}

function tradeNpcCatalogEntries(options) {
  return catalogEntries(options, ["tradeNpcs", "tradeNpcCatalog"]);
}

function tradeNpcLookupValues(entry) {
  return [
    entry?.npcKey,
    entry?.npcName,
    entry?.assignedRegionLabel,
    entry?.sellOriginLabel,
  ];
}

function resolvedUnprefixedSpecialTerm(entry) {
  const resolved = resolvedSpecialItemReference(entry);
  if (!resolved) {
    return null;
  }
  if (resolved.kind === "item-group") {
    return {
      kind: "item-group",
      itemIds: resolved.itemIds,
      sourceKinds: Array.isArray(resolved.sourceKinds) ? resolved.sourceKinds : [],
    };
  }
  return resolved.kind === "fish"
    ? { kind: "fish", fishId: resolved.id }
    : { kind: "item", itemId: resolved.id };
}

function namedUnprefixedRecord(entry, entryIndex, kind, id) {
  if (id == null) {
    return null;
  }
  return {
    key: `${kind}:${entryIndex}`,
    itemId: id,
    itemName: entry?.name,
    values: [
      entry?.name,
      entry?.label,
      entry?.slug,
      ...(Array.isArray(entry?.labels) ? entry.labels : []),
    ],
    value: {
      matchKind: "default",
      term: kind === "fish" || itemEntryIsFish(entry)
        ? { kind: "fish", fishId: id }
        : { kind: "item", itemId: id },
    },
  };
}

const PREFIX_INDEX_OPTIONS = Object.freeze({ mode: "prefix" });
const UNPREFIXED_FISH_PREPARED_VALUES = new WeakMap();
const UNPREFIXED_ITEM_PREPARED_VALUES = new WeakMap();
const UNPREFIXED_SEMANTIC_PREPARED_VALUES = new WeakMap();

const indexZoneNames = createQueryCatalogIndexCache(entry => {
  const zoneRgb = isPlainObject(entry) ? zoneEntryRgb(entry) : null;
  return zoneRgb == null ? null : { zoneRgb, values: zoneEntryLabelLookupValues(entry) };
});

const indexUnprefixedSpecial = createQueryCatalogIndexCache((entry, entryIndex) => {
  const term = isPlainObject(entry) ? resolvedUnprefixedSpecialTerm(entry) : null;
  return term && {
    key: `special:${entryIndex}`,
    values: specialItemReferenceLookupValues(entry),
    value: { matchKind: "default", term },
  };
}, PREFIX_INDEX_OPTIONS);
const indexUnprefixedZones = createQueryCatalogIndexCache((entry, entryIndex) => {
  const zoneRgb = isPlainObject(entry) ? zoneEntryRgb(entry) : null;
  if (zoneRgb == null) {
    return null;
  }
  const labelValues = zoneEntryLabelLookupValues(entry);
  const structuredValues = zoneEntryStructuredLookupValues(entry);
  return {
    key: `zone:${entryIndex}`,
    values: [...labelValues, ...structuredValues],
    value: {
      matchKind: "zone",
      labelValues,
      structuredValues,
      term: { kind: "zone", zoneRgb },
    },
  };
}, PREFIX_INDEX_OPTIONS);
const indexUnprefixedFish = createQueryCatalogIndexCache((entry, entryIndex) => {
  const record = isPlainObject(entry)
    ? namedUnprefixedRecord(
        entry,
        entryIndex,
        "fish",
        fishEntryReferenceId(entry, entry?.fishId ?? entry?.id),
      )
    : null;
  const preparedValues = UNPREFIXED_FISH_PREPARED_VALUES.get(entry);
  return record && Array.isArray(preparedValues)
    ? { ...record, preparedValues }
    : record;
}, PREFIX_INDEX_OPTIONS);
const indexUnprefixedItems = createQueryCatalogIndexCache((entry, entryIndex) => {
  const record = isPlainObject(entry)
    ? namedUnprefixedRecord(
        entry,
        entryIndex,
        "item",
        itemEntryReferenceId(entry, entry?.itemId ?? entry?.id),
      )
    : null;
  const preparedValues = UNPREFIXED_ITEM_PREPARED_VALUES.get(entry);
  return record && Array.isArray(preparedValues)
    ? { ...record, preparedValues }
    : record;
}, PREFIX_INDEX_OPTIONS);
const indexUnprefixedSemantic = createQueryCatalogIndexCache((entry, entryIndex) => {
  const layerId = isPlainObject(entry) ? semanticLayerValue(entry) : "";
  const fieldId = semanticFieldIdValue(entry);
  if (!layerId || layerId.toLowerCase() === "zone_mask" || fieldId == null) {
    return null;
  }
  return {
    key: `semantic:${entryIndex}`,
    values: semanticFieldLookupValues(entry),
    ...(Array.isArray(UNPREFIXED_SEMANTIC_PREPARED_VALUES.get(entry))
      ? { preparedValues: UNPREFIXED_SEMANTIC_PREPARED_VALUES.get(entry) }
      : {}),
    value: {
      matchKind: "default",
      term: { kind: "semantic", layerId, fieldId },
    },
  };
}, PREFIX_INDEX_OPTIONS);
const indexUnprefixedTradeNpcs = createQueryCatalogIndexCache((entry, entryIndex) => {
  const npcKey = isPlainObject(entry)
    ? parseSearchExpressionPositiveInteger(entry?.npcKey)
    : null;
  if (npcKey == null) {
    return null;
  }
  return {
    key: `trade-npc:${entryIndex}`,
    values: tradeNpcLookupValues(entry),
    value: {
      matchKind: "default",
      term: tradeNpcSearchTermForKey(npcKey),
    },
  };
}, PREFIX_INDEX_OPTIONS);

function alignedPreparedValueAssignments(entries, values, cache, label) {
  if (!Array.isArray(values) || entries.length !== values.length) {
    throw new TypeError(`${label} prepared values must align with the catalog revision`);
  }
  return entries.map((entry, index) => ({
    cache,
    entry,
    values: values[index],
    label: `${label}[${index}]`,
  }));
}

export function createSearchExpressionCatalogPreparedValuesTransaction(catalog, prepared) {
  const source = isPlainObject(catalog) ? catalog : {};
  const fish = Array.isArray(source.fish) ? source.fish : [];
  const items = Array.isArray(source.items) ? source.items : [];
  const semanticTerms = Array.isArray(source.semanticTerms) ? source.semanticTerms : [];
  return createPreparedQueryMatchCacheTransaction([
    ...alignedPreparedValueAssignments(
      fish,
      prepared?.fishExact,
      UNPREFIXED_FISH_PREPARED_VALUES,
      "fish expression",
    ),
    ...alignedPreparedValueAssignments(
      items,
      prepared?.itemExact,
      UNPREFIXED_ITEM_PREPARED_VALUES,
      "item expression",
    ),
    ...alignedPreparedValueAssignments(
      semanticTerms,
      prepared?.semanticExact,
      UNPREFIXED_SEMANTIC_PREPARED_VALUES,
      "semantic expression",
    ),
  ]);
}

function indexedUnprefixedCatalog(catalog, indexCatalog) {
  return catalog.available
    ? { available: true, index: indexCatalog(catalog.entries) }
    : { available: false, index: null };
}

function unprefixedCatalogIndexes(options) {
  return [
    indexedUnprefixedCatalog(specialItemReferenceEntries(options), indexUnprefixedSpecial),
    indexedUnprefixedCatalog(zoneCatalogEntries(options), indexUnprefixedZones),
    indexedUnprefixedCatalog(
      catalogEntries(options, ["fishCatalog", "catalogFish", "fish"]),
      indexUnprefixedFish,
    ),
    indexedUnprefixedCatalog(
      catalogEntries(options, ["itemCatalog", "catalogItems", "items"]),
      indexUnprefixedItems,
    ),
    indexedUnprefixedCatalog(semanticCatalogEntries(options), indexUnprefixedSemantic),
    indexedUnprefixedCatalog(tradeNpcCatalogEntries(options), indexUnprefixedTradeNpcs),
  ];
}

export function prepareSearchExpressionCatalogIndexes(catalog) {
  const startedAt = globalThis.performance?.now?.() ?? null;
  const [specialItems, zones, fish, items, semanticTerms, tradeNpcs] =
    unprefixedCatalogIndexes(catalog);
  const durationMs = startedAt == null
    ? null
    : Math.max(0, globalThis.performance.now() - startedAt);
  return Object.freeze({
    durationMs,
    indexedRecords: Object.freeze({
      specialItems: specialItems.index?.records?.length ?? 0,
      zones: zones.index?.records?.length ?? 0,
      fish: fish.index?.records?.length ?? 0,
      items: items.index?.records?.length ?? 0,
      semanticTerms: semanticTerms.index?.records?.length ?? 0,
      tradeNpcs: tradeNpcs.index?.records?.length ?? 0,
    }),
  });
}

function matchUnprefixedIndexRecord(record, normalized, slug, queryTokens) {
  const value = record?.value;
  if (value?.matchKind !== "zone") {
    return {
      ...lookupCandidateMatch(record?.values, normalized, slug, queryTokens),
      term: value?.term,
    };
  }
  const labelMatch = lookupCandidateMatch(value.labelValues, normalized, slug, queryTokens);
  const structuredRank = lookupValueMatchRank(value.structuredValues, normalized, slug);
  const match = structuredRank > labelMatch.rank
    ? { rank: structuredRank }
    : labelMatch;
  return { ...match, term: value.term };
}

export function resolveSearchExpressionItemNameGroup(rawValue, options) {
  const fish = catalogEntries(options, ["fishCatalog", "catalogFish", "fish"]);
  const items = catalogEntries(options, ["itemCatalog", "catalogItems", "items"]);
  return findQueryItemNameGroup([
    ...(fish.available ? [indexUnprefixedFish(fish.entries)] : []),
    ...(items.available ? [indexUnprefixedItems(items.entries)] : []),
  ], rawValue);
}

export function resolveSearchExpressionUnprefixedCatalogTerm(rawValue, options) {
  const raw = String(rawValue ?? "").trim();
  if (
    !raw ||
    raw.includes("%") ||
    /^[=!<>~]/.test(raw) ||
    isSearchExpressionReservedKeywordPrefix(raw) ||
    parseSearchExpressionPositiveInteger(raw) != null ||
    parseSearchExpressionZoneRgb(raw) != null
  ) {
    return { status: "invalid" };
  }
  const normalized = normalizeSearchExpressionLookupText(raw);
  const slug = slugifySearchExpressionLookupText(raw);
  if (!normalized && !slug) {
    return { status: "invalid" };
  }
  const group = resolveSearchExpressionItemNameGroup(raw, options);
  if (group) return { status: "resolved", term: { kind: "item-group", itemIds: group.itemIds } };
  const indexedCatalogs = unprefixedCatalogIndexes(options);
  const available = indexedCatalogs.some((catalog) => catalog.available);
  if (!available) {
    return { status: "catalog_unavailable" };
  }
  const queryTokens = searchExpressionLookupTokens(normalized);
  const resolved = resolveQueryCatalogReference(
    indexedCatalogs.filter(catalog => catalog.available).map(catalog => catalog.index),
    raw,
    record => {
      const candidate = matchUnprefixedIndexRecord(record, normalized, slug, queryTokens);
      return candidate.rank > LOOKUP_MATCH_NONE ? candidateIdentityKey(candidate) : "";
    },
    (record, previous) => {
      const candidate = matchUnprefixedIndexRecord(record, normalized, slug, queryTokens);
      const existing = matchUnprefixedIndexRecord(previous, normalized, slug, queryTokens);
      return candidate.rank > existing.rank ||
        (candidate.rank === existing.rank &&
          ((Number.isInteger(candidate.windowStart) &&
              (!Number.isInteger(existing.windowStart) || candidate.windowStart < existing.windowStart)) ||
            (candidate.windowStart === existing.windowStart &&
              Number(candidate.windowScore || 0) > Number(existing.windowScore || 0))));
    },
  );
  return resolved.status === "resolved"
    ? { status: "resolved", term: resolved.record.value.term }
    : resolved;
}

function semanticCatalogEntries(options) {
  return catalogEntries(options, [
    "semanticTerms",
    "semanticCatalog",
    "catalogSemanticTerms",
    "semantic",
  ]);
}

function semanticLayerValue(entry) {
  return String(entry?.layerId ?? entry?.layer_id ?? entry?.layer ?? "").trim();
}

function semanticFieldIdValue(entry) {
  return parseSearchExpressionPositiveInteger(
    entry?.fieldId ?? entry?.field_id ?? entry?.id ?? entry?.value,
  );
}

function valueMatchesLookup(value, normalized, slug) {
  if (value == null) {
    return false;
  }
  return (
    normalizeSearchExpressionLookupText(value) === normalized ||
    slugifySearchExpressionLookupText(value) === slug
  );
}

function semanticLayerMatches(entry, normalized, slug) {
  return [
    semanticLayerValue(entry),
    entry?.layerName,
    entry?.layer_name,
    entry?.layerLabel,
    entry?.layer_label,
  ].some((value) => valueMatchesLookup(value, normalized, slug));
}

function semanticFieldLookupValues(entry) {
  return [
    entry?.label,
    entry?.name,
    entry?.fieldName,
    entry?.field_name,
    entry?.fieldLabel,
    entry?.field_label,
    entry?.slug,
    entry?.description,
    entry?.searchText,
    entry?.search_text,
  ];
}

function semanticFieldMatchesExact(entry, normalized, slug) {
  return semanticFieldLookupValues(entry).some((value) => valueMatchesLookup(value, normalized, slug));
}

export function resolveSearchExpressionFishId(rawValue, options) {
  return resolveCatalogId(
    rawValue,
    options,
    ["fishCatalog", "catalogFish", "fish"],
    ["fishId", "fish_id", "id", "itemId", "item_id"],
    ["name", "label", "labels", "slug"],
  );
}

export function resolveSearchExpressionItemId(rawValue, options) {
  return resolveCatalogId(
    rawValue,
    options,
    ["itemCatalog", "catalogItems", "items"],
    ["itemId", "item_id", "id", "fishId", "fish_id"],
    ["name", "label", "labels", "slug"],
  );
}

export function resolveSearchExpressionTradeNpcKey(rawValue, options) {
  const numeric = parseSearchExpressionPositiveInteger(rawValue);
  const catalog = tradeNpcCatalogEntries(options);
  if (numeric != null) {
    return { status: "resolved", npcKey: numeric, layerId: TRADE_NPC_LAYER_ID };
  }
  const normalized = normalizeSearchExpressionLookupText(rawValue);
  const slug = slugifySearchExpressionLookupText(rawValue);
  if (!normalized && !slug) {
    return { status: "invalid" };
  }
  if (!catalog.available) {
    return { status: "catalog_unavailable" };
  }
  const exactNpcs = new Map();
  const fuzzyNpcs = new Map();
  for (const entry of catalog.entries) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const npcKey = parseSearchExpressionPositiveInteger(entry?.npcKey ?? entry?.npc_key);
    if (npcKey == null) {
      continue;
    }
    const names = [entry?.npcName, entry?.npc_name, entry?.name, entry?.label];
    if (lookupValueMatchesExact(names, normalized, slug)) {
      exactNpcs.set(npcKey, { npcKey, layerId: TRADE_NPC_LAYER_ID });
    } else if (lookupValueMatchesFuzzy(names, normalized, slug)) {
      fuzzyNpcs.set(npcKey, { npcKey, layerId: TRADE_NPC_LAYER_ID });
    }
  }
  if (exactNpcs.size === 1) {
    return { status: "resolved", ...[...exactNpcs.values()][0] };
  }
  if (exactNpcs.size > 1 || fuzzyNpcs.size > 1) {
    return { status: "ambiguous" };
  }
  return fuzzyNpcs.size === 1
    ? { status: "resolved", ...[...fuzzyNpcs.values()][0] }
    : { status: "unknown" };
}

export function resolveSearchExpressionFishOrItemReference(rawValue, options, preferredKind = "item") {
  const group = resolveSearchExpressionItemNameGroup(rawValue, options);
  if (group) return { status: "resolved", kind: "item-group", itemIds: group.itemIds };
  const resolved = resolveFishOrItemReferenceFromCatalog(rawValue, options);
  if (resolved) {
    return resolved;
  }
  const numeric = parseSearchExpressionPositiveInteger(rawValue);
  if (numeric != null) {
    if (
      !catalogHasAvailableEntries(options, ["fishCatalog", "catalogFish", "fish"]) &&
      !catalogHasAvailableEntries(options, ["itemCatalog", "catalogItems", "items"])
    ) {
      return {
        status: "resolved",
        id: numeric,
        kind: String(preferredKind || "").trim().toLowerCase() === "fish" ? "fish" : "item",
      };
    }
    return {
      status: "resolved",
      id: numeric,
      kind: String(preferredKind || "").trim().toLowerCase() === "fish" ? "fish" : "item",
    };
  }
  return {
    status: "catalog_unavailable",
  };
}

export function resolveSearchExpressionZoneReference(rawValue, options, { multiple = false } = {}) {
  const raw = String(rawValue ?? "").trim();
  if (!raw) {
    return { status: "invalid" };
  }
  const parsedZoneRgb = parseSearchExpressionZoneRgb(raw);
  if (parsedZoneRgb != null) {
    return { status: "resolved", zoneRgb: parsedZoneRgb };
  }
  const catalog = zoneCatalogEntries(options);
  if (!catalog.available) {
    return { status: "catalog_unavailable" };
  }
  if (multiple) {
    const zoneRgbs = [...new Set(matchingQueryCatalogRecords(indexZoneNames(catalog.entries), raw)
      .map(record => record.zoneRgb))];
    return zoneRgbs.length ? { status: "resolved", zoneRgbs } : { status: "unknown_zone" };
  }
  const normalized = normalizeSearchExpressionLookupText(raw);
  const slug = slugifySearchExpressionLookupText(raw);
  const fuzzyMatches = new Map();
  for (const entry of catalog.entries) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const zoneRgb = zoneEntryRgb(entry);
    if (zoneRgb == null) {
      continue;
    }
    if (
      lookupValueMatchesExact(zoneEntryLabelLookupValues(entry), normalized, slug) ||
      lookupStructuredValueMatchesExact(zoneEntryStructuredLookupValues(entry), normalized)
    ) {
      return { status: "resolved", zoneRgb };
    }
    if (lookupValueMatchesFuzzy(zoneEntryLabelLookupValues(entry), normalized, slug)) {
      fuzzyMatches.set(zoneRgb, zoneRgb);
    }
  }
  if (fuzzyMatches.size === 1) {
    return { status: "resolved", zoneRgb: [...fuzzyMatches.keys()][0] };
  }
  return { status: "unknown_zone" };
}

export function resolveSearchExpressionPatchId(rawValue, options) {
  const raw = normalizePatchId(rawValue);
  if (!raw) {
    return { status: "invalid" };
  }
  const catalog = catalogEntries(options, ["patchCatalog", "catalogPatches", "patches"]);
  if (!catalog.available) {
    // Syntax parsing is intentionally catalog-independent. Interactive/runtime
    // callers provide a patch catalog and therefore take the validated path.
    return { status: "resolved", patchId: raw };
  }
  const normalized = normalizeSearchExpressionLookupText(raw);
  const slug = slugifySearchExpressionLookupText(raw);
  const matches = new Map();
  for (const entry of catalog.entries) {
    if (!isPlainObject(entry)) {
      continue;
    }
    const patchId = normalizePatchId(
      entry.patchId ?? entry.patch_id ?? entry.id ?? entry.value,
    );
    if (!patchId) {
      continue;
    }
    const values = [
      patchId,
      entry.patchName,
      entry.patch_name,
      entry.name,
      entry.label,
    ];
    if (values.some((value) => lookupValueMatchesExact(value, normalized, slug))) {
      matches.set(patchId, patchId);
    }
  }
  if (matches.size === 1) {
    return { status: "resolved", patchId: [...matches.values()][0] };
  }
  return { status: matches.size > 1 ? "ambiguous" : "unknown_patch" };
}

export function resolveSearchExpressionSemanticField(layerValue, fieldValue, options) {
  const rawLayer = String(layerValue ?? "").trim();
  const rawField = String(fieldValue ?? "").trim();
  if (!rawLayer || !rawField) {
    return { status: "invalid" };
  }

  const fieldId = parseSearchExpressionPositiveInteger(rawField);
  const catalog = semanticCatalogEntries(options);
  if (!catalog.available) {
    return fieldId != null
      ? { status: "resolved", layerId: rawLayer, fieldId }
      : { status: "catalog_unavailable" };
  }

  const layerLookup = normalizeSearchExpressionLookupText(rawLayer);
  const layerSlug = slugifySearchExpressionLookupText(rawLayer);
  const fieldLookup = normalizeSearchExpressionLookupText(rawField);
  const fieldSlug = slugifySearchExpressionLookupText(rawField);
  const layerEntries = catalog.entries.filter((entry) =>
    isPlainObject(entry) && semanticLayerMatches(entry, layerLookup, layerSlug)
  );
  if (!layerEntries.length) {
    return { status: "unknown_layer" };
  }

  const fuzzyMatches = new Map();
  for (const entry of layerEntries) {
    const candidateFieldId = semanticFieldIdValue(entry);
    if (candidateFieldId == null) {
      continue;
    }
    if (fieldId != null && candidateFieldId === fieldId) {
      return {
        status: "resolved",
        layerId: semanticLayerValue(entry) || rawLayer,
        fieldId: candidateFieldId,
      };
    }
    if (fieldId == null && semanticFieldMatchesExact(entry, fieldLookup, fieldSlug)) {
      return {
        status: "resolved",
        layerId: semanticLayerValue(entry) || rawLayer,
        fieldId: candidateFieldId,
      };
    }
    if (fieldId == null && lookupValueMatchesFuzzy(semanticFieldLookupValues(entry), fieldLookup, fieldSlug)) {
      fuzzyMatches.set(candidateFieldId, {
        layerId: semanticLayerValue(entry) || rawLayer,
        fieldId: candidateFieldId,
      });
    }
  }

  if (fuzzyMatches.size === 1) {
    return { status: "resolved", ...[...fuzzyMatches.values()][0] };
  }
  return { status: "unknown_field" };
}
